import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import PDFViewer from '../components/document/PDFViewer';
import AnonymousUserInterface from '../components/auth/AnonymousUserInterface';
import { logger } from '../services/logger';
import { validator, ValidationError } from '../services/validation';
import { performanceMonitor } from '../services/performance';
import { DocumentAPI } from '../services/api';

interface AnonymousDocumentMetadata {
  documentId: string;
  name: string;
  pageCount: number;
  fileSize: number;
  createdAt: string;
  expiresAt: string;
  isAnonymous: boolean;
}

const AnonymousViewerPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [metadata, setMetadata] = useState<AnonymousDocumentMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInterface, setShowInterface] = useState(false);

  const { documents } = useApi();
  
  // Transform API response format to frontend interface format
  const transformAnonymousDocument = (apiDoc: any): AnonymousDocumentMetadata => ({
    documentId: apiDoc.document_id,
    name: apiDoc.name || 'Untitled Document',
    pageCount: apiDoc.page_count,
    fileSize: apiDoc.file_size || 0,
    createdAt: apiDoc.created_at || '',
    expiresAt: apiDoc.expires_at || '',
    isAnonymous: true
  });
  
  // Production-ready logging and monitoring
  const correlationId = logger.getCorrelationId();
  const pageTimer = performanceMonitor.startTimer('AnonymousViewerPage_lifecycle', correlationId);

  useEffect(() => {
    logger.info('AnonymousViewerPage component initialized', {
      correlationId,
      operation: 'componentInit',
      documentId,
      metadata: {
        hasDocumentId: !!documentId,
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    });

    // Validate document ID immediately
    if (!documentId) {
      const error = 'Document ID is required';
      logger.error('AnonymousViewerPage validation failed', {
        correlationId,
        operation: 'inputValidation',
        error,
        metadata: { documentId }
      });
      setError(error);
      setLoading(false);
      performanceMonitor.endTimer(pageTimer, { success: false, error });
      return;
    }

    // Validate document ID format
    if (!validator.validateDocumentId(documentId, { correlationId })) {
      const error = 'Invalid document ID format';
      logger.error('AnonymousViewerPage validation failed', {
        correlationId,
        operation: 'inputValidation',
        error,
        documentId: documentId.substring(0, 8) + '...' // Log partial for security
      });
      setError(error);
      setLoading(false);
      performanceMonitor.endTimer(pageTimer, { success: false, error });
      return;
    }

    loadDocumentMetadata();

    // Cleanup on unmount
    return () => {
      logger.info('AnonymousViewerPage component unmounted', {
        correlationId,
        operation: 'componentUnmount',
        documentId,
        metadata: {
          totalDuration: Date.now() - pageTimer.startTime,
          hadMetadata: !!metadata,
          showingInterface: showInterface
        }
      });
    };
  }, [documentId]);

  const loadDocumentMetadata = async () => {
    const loadTimer = performanceMonitor.startTimer('loadDocumentMetadata', correlationId);
    
    try {
      setLoading(true);
      setError('');

      logger.info('Loading anonymous document metadata', {
        correlationId,
        operation: 'loadDocumentMetadata',
        documentId,
        metadata: {
          timestamp: new Date().toISOString()
        }
      });

      // Use production-ready API service
      const apiData = await DocumentAPI.getAnonymousDocumentMetadata(documentId!);
      
      // Transform API response to frontend format
      const data = transformAnonymousDocument(apiData);
      
      // Validate received metadata
      if (!data || !data.documentId || !data.pageCount) {
        throw new Error('Invalid metadata received from server');
      }

      // Additional validation
      if (!validator.validatePageNumber(data.pageCount, 10000, { correlationId })) {
        throw new Error('Invalid page count in metadata');
      }

      setMetadata(data);

      // Track successful load
      performanceMonitor.trackUserInteraction(
        'loadAnonymousDocument',
        'anonymousViewerPage',
        undefined,
        { 
          documentId, 
          pageCount: data.pageCount, 
          fileSize: data.fileSize,
          hasExpiration: !!data.expiresAt
        },
        correlationId
      );

      logger.info('Anonymous document metadata loaded successfully', {
        correlationId,
        operation: 'loadDocumentMetadata',
        documentId,
        metadata: {
          documentName: data.name,
          pageCount: data.pageCount,
          fileSize: data.fileSize,
          expiresAt: data.expiresAt,
          createdAt: data.createdAt
        }
      });

      performanceMonitor.endTimer(loadTimer, { 
        success: true, 
        metadata: { 
          pageCount: data.pageCount, 
          fileSize: data.fileSize 
        } 
      });
      performanceMonitor.endTimer(pageTimer, { success: true });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load document';
      
      logger.error('Failed to load anonymous document metadata', {
        correlationId,
        operation: 'loadDocumentMetadata',
        documentId,
        error: errorMessage,
        stack: err.stack,
        metadata: {
          status: err.response?.status,
          statusText: err.response?.statusText
        }
      });

      if (err.response?.status === 404) {
        setError('Document not found or has expired.');
      } else if (err.response?.status === 403) {
        setError('Access denied to this document.');
      } else {
        setError(errorMessage);
      }

      performanceMonitor.endTimer(loadTimer, { 
        success: false, 
        error: errorMessage 
      });
      performanceMonitor.endTimer(pageTimer, { 
        success: false, 
        error: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    try {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    } catch (error) {
      logger.warn('Error formatting file size', {
        correlationId,
        operation: 'formatFileSize',
        error: error instanceof Error ? error.message : 'Unknown error',
        metadata: { bytes }
      });
      return 'Unknown size';
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      logger.warn('Error formatting date', {
        correlationId,
        operation: 'formatDate',
        error: error instanceof Error ? error.message : 'Unknown error',
        metadata: { dateString }
      });
      return 'Invalid date';
    }
  };

  const copyShareLink = () => {
    const copyTimer = performanceMonitor.startTimer('copyShareLink', correlationId);
    
    try {
      const shareUrl = `${window.location.origin}/view/${documentId}`;
      
      // Validate URL before copying
      const urlValidation = validator.validateAndSanitizeString(shareUrl, 2000, true, { correlationId });
      if (!urlValidation.isValid) {
        throw new Error('Invalid share URL generated');
      }

      navigator.clipboard.writeText(shareUrl);

      // Track user interaction
      performanceMonitor.trackUserInteraction(
        'copyShareLink',
        'anonymousViewerPage',
        undefined,
        { documentId, shareUrl: shareUrl.substring(0, 50) + '...' }, // Log partial URL for security
        correlationId
      );

      logger.info('Share link copied to clipboard', {
        correlationId,
        operation: 'copyShareLink',
        documentId,
        metadata: {
          urlLength: shareUrl.length
        }
      });

      performanceMonitor.endTimer(copyTimer, { success: true });
      
      // Could add a toast notification here
    } catch (error) {
      logger.error('Failed to copy share link', {
        correlationId,
        operation: 'copyShareLink',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      performanceMonitor.endTimer(copyTimer, { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      
      // Fallback: show alert with URL
      const shareUrl = `${window.location.origin}/view/${documentId}`;
      alert(`Copy this link: ${shareUrl}`);
    }
  };

  if (loading) {
    logger.debug('AnonymousViewerPage showing loading state', {
      correlationId,
      operation: 'renderLoading',
      documentId
    });
    return <LoadingSpinner message="Loading document..." />;
  }

  if (error) {
    logger.info('AnonymousViewerPage showing error state', {
      correlationId,
      operation: 'renderError',
      documentId,
      error
    });
    
    return (
      <div className="page-container">
        <div className="text-center">
          <ErrorMessage message={error} />
          
          <div className="mt-4">
            <h3>Document Not Available</h3>
            <p className="text-muted">
              This document may have expired or been removed.
            </p>
            
            <div className="mt-4">
              <Link 
                to="/"
                className="btn btn-primary"
                onClick={() => {
                  performanceMonitor.trackUserInteraction(
                    'navigateToUpload',
                    'anonymousViewerPage',
                    undefined,
                    { fromError: true, documentId },
                    correlationId
                  );
                  logger.info('User navigating to upload from error state', {
                    correlationId,
                    operation: 'navigateToUpload',
                    documentId
                  });
                }}
              >
                Upload New Document
              </Link>
              <Link 
                to="/register" 
                className="btn btn-success" 
                style={{ marginLeft: '10px' }}
                onClick={() => {
                  performanceMonitor.trackUserInteraction(
                    'navigateToRegister',
                    'anonymousViewerPage',
                    undefined,
                    { fromError: true, documentId },
                    correlationId
                  );
                  logger.info('User navigating to register from error state', {
                    correlationId,
                    operation: 'navigateToRegister',
                    documentId
                  });
                }}
              >
                Create Account
              </Link>
            </div>
          </div>

          {/* Show anonymous user interface even on error */}
          <div className="mt-5">
            <AnonymousUserInterface
              showUploadPrompt={true}
              showRegistrationPrompts={true}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!metadata) {
    logger.warn('AnonymousViewerPage no metadata available', {
      correlationId,
      operation: 'renderNoMetadata',
      documentId
    });
    return (
      <div className="page-container">
        <ErrorMessage message="Document metadata not available" />
      </div>
    );
  }

  // Toggle between viewer and interface with logging
  if (showInterface) {
    logger.debug('AnonymousViewerPage showing interface view', {
      correlationId,
      operation: 'renderInterface',
      documentId,
      metadata: {
        documentName: metadata.name,
        pageCount: metadata.pageCount
      }
    });
    
    return (
      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">{metadata.name}</h1>
          <p className="page-subtitle">
            {formatFileSize(metadata.fileSize)} • {metadata.pageCount} pages • 
            Uploaded {formatDate(metadata.createdAt)}
          </p>
        </div>

        <div className="d-flex justify-content-center mb-4">
          <div className="btn-group">
            <button 
              className="btn btn-primary"
              onClick={() => {
                performanceMonitor.trackUserInteraction(
                  'switchToViewer',
                  'anonymousViewerPage',
                  undefined,
                  { documentId },
                  correlationId
                );
                logger.info('User switching to document viewer', {
                  correlationId,
                  operation: 'switchToViewer',
                  documentId
                });
                setShowInterface(false);
              }}
            >
              📄 View Document
            </button>
            <button 
              className="btn btn-secondary active"
              onClick={() => {
                performanceMonitor.trackUserInteraction(
                  'stayOnInterface',
                  'anonymousViewerPage',
                  undefined,
                  { documentId },
                  correlationId
                );
                setShowInterface(true);
              }}
            >
              ℹ️ Document Info
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-center">
          <div style={{ maxWidth: '800px', width: '100%' }}>
            <AnonymousUserInterface
              documentId={documentId}
              expiresAt={metadata.expiresAt}
              showUploadPrompt={true}
              showRegistrationPrompts={true}
            />
          </div>
        </div>
      </div>
    );
  }

  logger.debug('AnonymousViewerPage showing document viewer', {
    correlationId,
    operation: 'renderViewer',
    documentId,
    metadata: {
      documentName: metadata.name,
      pageCount: metadata.pageCount
    }
  });

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Document Header with Expiration Warning */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e9ecef', 
        flexShrink: 0
      }}>
        {/* Document Info */}
        <div style={{ padding: '15px 20px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{metadata.name}</h2>
              <div className="text-muted" style={{ fontSize: '14px' }}>
                {formatFileSize(metadata.fileSize)} • {metadata.pageCount} pages • 
                Uploaded {formatDate(metadata.createdAt)}
              </div>
            </div>
            
            <div className="d-flex" style={{ gap: '10px' }}>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => {
                  performanceMonitor.trackUserInteraction(
                    'switchToInterface',
                    'anonymousViewerPage',
                    undefined,
                    { documentId },
                    correlationId
                  );
                  logger.info('User switching to document interface', {
                    correlationId,
                    operation: 'switchToInterface',
                    documentId
                  });
                  setShowInterface(true);
                }}
                title="View document information and features"
              >
                ℹ️ Info
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  performanceMonitor.trackUserInteraction(
                    'copyShareLink',
                    'anonymousViewerPage',
                    undefined,
                    { documentId, source: 'header' },
                    correlationId
                  );
                  copyShareLink();
                }}
              >
                📋 Copy Link
              </button>
              <Link 
                to="/register" 
                className="btn btn-success"
                onClick={() => {
                  performanceMonitor.trackUserInteraction(
                    'navigateToRegister',
                    'anonymousViewerPage',
                    undefined,
                    { documentId, source: 'header' },
                    correlationId
                  );
                  logger.info('User navigating to register from header', {
                    correlationId,
                    operation: 'navigateToRegister',
                    documentId
                  });
                }}
              >
                💾 Save Forever
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <PDFViewer 
          documentId={documentId!}
          pageCount={metadata.pageCount}
          isAnonymous={true}
        />
      </div>

      {/* Compact Anonymous User Interface at bottom */}
      <div style={{ 
        background: '#f8f9fa', 
        borderTop: '1px solid #e9ecef',
        padding: '10px 20px',
        flexShrink: 0
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center" style={{ gap: '15px', fontSize: '14px' }}>
            <span>
              ⏰ <strong>Expires:</strong> {formatDate(metadata.expiresAt)}
            </span>
            <span className="text-muted">|</span>
            <span>📊 Anonymous document with basic analytics</span>
          </div>
          
          <div className="d-flex" style={{ gap: '10px' }}>
            <Link 
              to="/register" 
              className="btn btn-sm btn-success"
              onClick={() => {
                performanceMonitor.trackUserInteraction(
                  'navigateToRegister',
                  'anonymousViewerPage',
                  undefined,
                  { documentId, source: 'footer' },
                  correlationId
                );
                logger.info('User navigating to register from footer', {
                  correlationId,
                  operation: 'navigateToRegister',
                  documentId
                });
              }}
            >
              Upgrade to Full Features
            </Link>
            <Link 
              to="/login" 
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                performanceMonitor.trackUserInteraction(
                  'navigateToLogin',
                  'anonymousViewerPage',
                  undefined,
                  { documentId, source: 'footer' },
                  correlationId
                );
                logger.info('User navigating to login from footer', {
                  correlationId,
                  operation: 'navigateToLogin',
                  documentId
                });
              }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnonymousViewerPage;